import { useState, useEffect } from "react";
import { useHabits } from "@/context/SupabaseHabitContext";
import { useMoodTracker, MoodType } from "@/context/MoodTrackerContext";
import { useIdentity } from "@/hooks/useIdentity";
import { format, subDays, isToday } from "date-fns";
import { Flame, Check, Heart, ChevronDown, Edit3, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import GridConfetti from "@/components/animations/GridConfetti";
import { getLocalDateString, getTodayLocalDateString } from "@/utils/dateUtils";

interface TopBarProps {
  showPausedHabits?: boolean;
  selectedDate?: Date;
}

export default function TopBar({ showPausedHabits = false, selectedDate = new Date() }: TopBarProps) {
  const { habits } = useHabits();
  const { getMoodForDate, setMoodForDate, deleteMoodForDate } = useMoodTracker();
  const { identityText, isLoading: isLoadingIdentity, isSaving: isSavingIdentity, saveIdentity } = useIdentity();
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiSource, setConfettiSource] = useState<{ x: number; y: number } | undefined>(undefined);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Identity text editing state
  const [isEditingIdentity, setIsEditingIdentity] = useState(false);
  const [tempIdentityText, setTempIdentityText] = useState(identityText);

  // Update temp text when identity text changes (from loading)
  useEffect(() => {
    if (!isEditingIdentity) {
      setTempIdentityText(identityText);
    }
  }, [identityText, isEditingIdentity]);

  // For streak calculations, always use all habits regardless of paused status
  // Streak should reflect overall consistency across all habits
  const allHabits = habits.filter(habit => habit.name.trim() !== '');
  
  // Use all habits for streak calculations
  const filteredHabits = allHabits;

  // Generate the last 5 days (including today)
  const getLast5Days = () => {
    const days = [];
    for (let i = 4; i >= 0; i--) {
      days.push(subDays(new Date(), i));
    }
    return days;
  };

  // Check if any habit was completed on a given date
  const isAnyHabitCompletedOnDate = (date: Date): boolean => {
    const dateString = format(date, 'yyyy-MM-dd');
    return filteredHabits.some(habit => 
      habit.completedDates.includes(dateString)
    );
  };

  // Calculate current streak
  const calculateStreak = (): number => {
    let streak = 0;
    let currentDate = subDays(new Date(), 1); // Start from yesterday instead of today
    
    // Calculate base streak from yesterday backwards
    while (true) {
      const dateString = getLocalDateString(currentDate);
      const hasAnyHabitCompleted = filteredHabits.some(habit => 
        habit.completedDates.includes(dateString)
      );
      
      if (hasAnyHabitCompleted) {
        streak++;
        currentDate = subDays(currentDate, 1);
      } else {
        break;
      }
    }
    
    // Check if user has completed any habit today and add 1 if so
    const todayString = getTodayLocalDateString();
    const hasCompletedHabitToday = filteredHabits.some(habit => 
      habit.completedDates.includes(todayString)
    );
    
    if (hasCompletedHabitToday) {
      streak += 1;
    }
    
    return streak;
  };

  const handleMoodChange = async (mood: MoodType, event: React.MouseEvent) => {
    setIsUpdating(true);
    
    try {
      const dateString = getLocalDateString(selectedDate);
      await setMoodForDate(dateString, mood);
      
      // Only trigger confetti if it's today's date
      if (isToday(selectedDate)) {
        const rect = event.currentTarget.getBoundingClientRect();
        const clickPosition = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
        setConfettiSource(clickPosition);
        setShowConfetti(true);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteMood = async () => {
    setIsUpdating(true);
    
    try {
      const dateString = getLocalDateString(selectedDate);
      await deleteMoodForDate(dateString);
    } finally {
      setIsUpdating(false);
    }
  };

  const last5Days = getLast5Days();
  const currentStreak = calculateStreak();
  const dateString = getLocalDateString(selectedDate);
  const currentMood = getMoodForDate(dateString);

  const getMoodColor = (mood: MoodType | null) => {
    if (!mood) return 'hsl(142, 71%, 45%)'; // Default green
    switch (mood) {
      case 'Easy': return 'hsl(142, 71%, 45%)'; // Green
      case 'Meh': return 'hsl(24, 95%, 65%)'; // Orange
      case 'Struggled': return 'hsl(0, 90%, 65%)'; // Red
      default: return 'hsl(142, 71%, 45%)';
    }
  };

  // Handle identity text save
  const handleSaveIdentity = async () => {
    const success = await saveIdentity(tempIdentityText);
    if (success) {
      setIsEditingIdentity(false);
    }
  };

  // Handle identity text cancel
  const handleCancelIdentity = () => {
    setTempIdentityText(identityText);
    setIsEditingIdentity(false);
  };

  // Handle edit button click
  const handleEditIdentity = () => {
    setTempIdentityText(identityText);
    setIsEditingIdentity(true);
  };

  return (
    <>
      {/* Identity Line */}
      <div className="text-center mb-4">
        {isEditingIdentity ? (
          <div className="flex items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground">I am a</span>
            <Input
              value={tempIdentityText}
              onChange={(e) => setTempIdentityText(e.target.value)}
              className="h-6 text-xs px-2 w-64 text-center"
              placeholder="e.g. software engineer, family man, student..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveIdentity();
                } else if (e.key === 'Escape') {
                  handleCancelIdentity();
                }
              }}
              autoFocus
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSaveIdentity}
              className="h-6 w-6 p-0"
              disabled={isSavingIdentity}
            >
              {isSavingIdentity ? (
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              ) : (
                <Save className="h-3 w-3" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleCancelIdentity}
              className="h-6 w-6 p-0"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 group">
            <p className="text-xs text-muted-foreground">
              {isLoadingIdentity ? (
                <span className="italic opacity-75">Loading...</span>
              ) : identityText ? (
                <>I am a <span className="underline">{identityText}</span></>
              ) : (
                <span className="italic opacity-75">Click to add your identity...</span>
              )}
            </p>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleEditIdentity}
              className="h-4 w-4 p-0"
            >
              <Edit3 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-8 gap-3">
        {/* Left: Mood Tracker */}
        <div className="flex-1 flex justify-start">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="flex items-center gap-1.5 h-8 px-3 text-xs"
                disabled={isUpdating}
              >
                <Heart 
                  className="h-3 w-3 flex-shrink-0" 
                  style={{ color: getMoodColor(currentMood) }}
                />
                <span className="text-[10px] sm:text-xs leading-tight">
                  {currentMood ? (
                    <span className="font-medium">{currentMood}</span>
                  ) : (
                    <>
                      <span className="block sm:inline">How was</span>
                      <span className="block sm:inline sm:ml-1">your day?</span>
                    </>
                  )}
                </span>
                {isUpdating ? (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent flex-shrink-0"></span>
                ) : (
                  <ChevronDown className="h-3 w-3 flex-shrink-0" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
              <DropdownMenuItem 
                onClick={(e) => handleMoodChange('Easy', e)}
                className="flex items-center gap-2"
              >
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'hsl(142, 71%, 45%)' }} />
                Easy
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => handleMoodChange('Meh', e)}
                className="flex items-center gap-2"
              >
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'hsl(24, 95%, 65%)' }} />
                Meh
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => handleMoodChange('Struggled', e)}
                className="flex items-center gap-2"
              >
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'hsl(0, 90%, 65%)' }} />
                Struggled
              </DropdownMenuItem>
              {currentMood && (
                <>
                  <div className="border-t my-1" />
                  <DropdownMenuItem 
                    onClick={handleDeleteMood}
                    className="text-destructive"
                  >
                    Remove mood
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Center: Last 5 days */}
        <div className="flex-1 flex justify-center">
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-muted-foreground">Last 5 days</span>
            <div className="flex gap-1">
              {last5Days.map((date, index) => {
                const isCompleted = isAnyHabitCompletedOnDate(date);
                const isCurrentDay = isToday(date);
                
                return (
                  <div
                    key={index}
                    className={`w-3 h-3 rounded-full flex items-center justify-center transition-all duration-200 ${
                      isCompleted
                        ? 'bg-green-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                    } ${
                      isCurrentDay ? 'ring-1 ring-blue-400' : ''
                    }`}
                    title={`${format(date, 'MMM d')}${isCurrentDay ? ' (Today)' : ''} - ${
                      isCompleted ? 'At least one habit completed' : 'No habits completed'
                    }`}
                  >
                    {isCompleted && (
                      <Check className="w-2 h-2 text-white" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: Streak */}
        <div className="flex-1 flex justify-end">
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-muted-foreground">current streak</span>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-sm text-orange-700 dark:text-orange-300">
                {currentStreak} day{currentStreak !== 1 ? 's' : ''}
              </span>
              <Flame className="h-4 w-4 text-orange-500" />
            </div>
          </div>
        </div>
      </div>

      <GridConfetti 
        run={showConfetti} 
        onConfettiComplete={() => setShowConfetti(false)} 
        sourcePosition={confettiSource}
      />
    </>
  );
} 